import React, { useState } from 'react';

import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import FileUploader from '../../Form/FileUploader';
import DocumentForm from './DocumentForm';
import Table from '../../Table';

import LoanFileService from '../../../services/LoanFileService';
import { useUiStore } from '../../../store';

const cols = [
  {
    title: 'Filename',
    key: 'filename',
  },
  {
    title: 'Date',
    key: 'date',
    type: 'date',
  },
  {
    title: 'Type',
    key: 'type',
  },
  {
    title: 'Notes',
    key: 'notes',
    textWrap: true,
  },
  {
    title: 'Borrower',
    key: 'borrower',
    format: (value) => {
      if (!value) {
        return '';
      }

      return `${value.firstName} ${value.lastName}`;
    },
  },
];

export default function Documents({ loanFile }) {
  const [files, setFiles] = useState([]);

  const navigate = useNavigate();

  const { data: documents, refetch } = useQuery({
    queryKey: ['loanFileDocuments', loanFile?.id],
    queryFn: () => LoanFileService.getLoanFileDocuments(loanFile?.id),
    enabled: !!loanFile?.id,
  });

  const notify = useUiStore((state) => state.notify);

  const { mutate: deleteDocument, isPending: isDeleting } = useMutation({
    mutationFn: ({ id, documentId }) =>
      LoanFileService.deleteLoanFileDocument({ id, documentId }),
    onSuccess: () => {
      notify({
        type: 'success',
        message: 'Document deleted successfully',
      });
      refetch();
    },
  });

  const showDialogModal = useUiStore((state) => state.showDialogModal);

  const actions = [
    {
      title: 'Edit',
      onClick: (row) =>
        navigate(`/loan-files/${row.loanFileId}/documents/${row.id}`),
      icon: 'edit',
    },
    {
      title: 'Delete',
      onClick: (row) =>
        showDialogModal({
          title: 'Delete Document',
          text: `Are you sure you want to delete this document?`,
          onConfirm: () =>
            deleteDocument({
              id: row.loanFileId,
              documentId: row.id,
            }),
        }),
      icon: 'delete',
      danger: true,
    },
  ];

  return (
    <div className="h-full bg-white">
      <div className="py-8 px-4 mx-auto max-w-2xl lg:py-16">
        <div className="mb-6">
          <FileUploader
            label="Documents"
            setFiles={setFiles}
            multiple
            accept=".pdf"
          />
        </div>
        <div className="flex flex-col gap-10">
          {Array.from(files).map((file) => {
            return (
              <DocumentForm
                key={file.name}
                file={file}
                setFiles={setFiles}
                loanFileId={loanFile?.id}
                disabled={isDeleting}
                refetch={refetch}
                loanFile={loanFile}
              />
            );
          })}
        </div>
      </div>

      <div className="p-3 sm:p-5 antialiased bg-white">
        <div className="mx-auto max-w-screen-xl px-4 lg:px-12">
          <Table
            cols={cols}
            rows={documents}
            countName="Documents"
            actions={actions}
          />
        </div>
      </div>
    </div>
  );
}
